import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { ConsentSection, CheckItem } from './consent/ConsentParts';

const API = process.env.REACT_APP_BACKEND_URL;

const overlayVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const modalVariants = { hidden: { opacity: 0, scale: 0.95, y: 20 }, visible: { opacity: 1, scale: 1, y: 0 } };

export function ConsentModal({ isOpen, onComplete, onClose }) {
  const [checks, setChecks] = useState({ terms: false, privacy: false, data: false, pilot: false });
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const allChecked = checks.terms && checks.privacy && checks.data && checks.pilot;
  const canSubmit = allChecked && signature.trim().length > 1;
  const toggle = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/consent/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agreed_to_terms: checks.terms, agreed_to_privacy: checks.privacy,
          agreed_to_data_processing: checks.data, agreed_to_pilot_participation: checks.pilot,
          signature_text: signature.trim()
        })
      });
      if (!res.ok) throw new Error('Failed to record consent');
      onComplete();
    } catch {
      setError('Failed to save consent. Please try again.');
    } finally { setLoading(false); }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div variants={overlayVariants} initial="hidden" animate="visible" exit="hidden"
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
        <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="hidden"
          className="bg-[#0E1830] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          <div className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 data-testid="consent-title" className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                SendPI Pilot Participation Agreement
              </h2>
              {onClose && <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>}
            </div>

            <div className="space-y-6 mb-8">
              <ConsentSection title="Pilot Program">
                You are participating in the SendPI pilot program. By continuing, you agree to: use SendPI for legitimate communication purposes, provide honest feedback when asked, allow SendPI to collect anonymous usage data to improve the product, and receive occasional emails about the pilot program.
              </ConsentSection>
              <ConsentSection title="What We Collect">
                We collect: your email address, how you use the product (which features, how often, acceptance rates), your communication tone patterns (not your message content). We NEVER collect or store the actual text of your messages. Your words never leave your device.
              </ConsentSection>
              <ConsentSection title="Technical Privacy Guarantee">
                SendPI uses a privacy-by-design architecture. Your message content NEVER leaves your device for storage. Analysis happens locally on your computer. Only your behavioral patterns are recorded — which tone you chose, whether you accepted a rewrite, what time of day you write, which website you were on. Never the words themselves. This is enforced by our code architecture, not just our policy. The system is technically incapable of storing your message content because it is excluded from our data model via a field whitelist.
              </ConsentSection>
              <ConsentSection title="Your Rights">
                You can delete your account and all your data at any time from Settings. You can withdraw from the pilot at any time. To request a copy of your data email support@sendpi.ai.
              </ConsentSection>
              <ConsentSection title="Pilot Terms">
                As a pilot participant, you agree that: SendPI may contact you for feedback, the product is in beta and may have bugs, features may change, your usage data helps us improve the product before public launch.
              </ConsentSection>
            </div>

            <div className="space-y-1 mb-8">
              <CheckItem checked={checks.terms} onToggle={() => toggle('terms')} testId="consent-check-terms" label="I agree to the Terms of Service" />
              <CheckItem checked={checks.privacy} onToggle={() => toggle('privacy')} testId="consent-check-privacy" label="I agree to the Privacy Policy" />
              <CheckItem checked={checks.data} onToggle={() => toggle('data')} testId="consent-check-data" label="I agree to the data collection described above" />
              <CheckItem checked={checks.pilot} onToggle={() => toggle('pilot')} testId="consent-check-pilot" label="I agree to participate in the SendPI pilot program" />
            </div>

            <div className="mb-6">
              <label className="block text-sm text-white/50 mb-2">Type your full name to sign this agreement</label>
              <input data-testid="consent-signature" type="text" value={signature} onChange={(e) => setSignature(e.target.value)}
                placeholder="Your full name"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50" />
            </div>

            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

            <button data-testid="consent-submit" onClick={handleSubmit} disabled={!canSubmit || loading}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${canSubmit ? 'bg-gradient-to-r from-violet-600 to-cyan-500 text-white hover:opacity-90' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}>
              {loading ? 'Signing...' : 'Sign and Continue →'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
